import { readPlatformSessionIdFromPageEntry } from '@/common/utils/platformFileSession'
import { normalizePlatformSessionId } from '@/common/utils/jsRequestSessionContext'
import { MAIN_FRAME_SLOT_ID } from '@/common/utils/mainFrameSlot'

export type ReloadPanelSessionContext = {
  sessionId?: string | number | null
  eventCoreId?: string | number | null
  /** onServerEvent pin: do not replace the parent core with the dialog page. */
  pinnedSessionId?: string | number | null
  /** Slot captured with the pin. */
  pinnedSlotId?: string | number | null
  /** Slot whose pageData this reload will actually read or write. */
  targetSlotId?: string | number | null
}

/**
 * sessionId для reload/panel: актуальный pagesData > context.sessionId.
 * eventCoreId из WS НЕ ИСПОЛЬЗУЕТСЯ - он часто указывает на уничтоженный auxiliary core после killDraft.
 * Swing parity: Swing использует action.getCore() (main session), не eventCoreId из WS.
 *
 * pinnedSessionId побеждает session страницы только для того же слота
 * (быстрый action переписал pageData между панелями). Чужой слот, и особенно
 * __main_frame__, остаётся на своём живом core.
 */
export function resolveReloadPanelSessionId(
  context: ReloadPanelSessionContext | undefined | null,
  pageEntries: unknown[],
  isSessionClosing?: (sessionId: string) => boolean,
): string | null {
  const pinned = normalizePlatformSessionId(context?.pinnedSessionId)
  if (pinned && pinnedReloadApplies(context) && !isSessionClosing?.(pinned)) {
    return pinned
  }

  for (const entry of pageEntries) {
    const fromHelper = readPlatformSessionIdFromPageEntry(entry)
    if (fromHelper) return fromHelper
    const direct = normalizePlatformSessionId((entry as { sessionId?: unknown } | null)?.sessionId)
    if (direct) return direct
  }

  const fromContext = normalizePlatformSessionId(context?.sessionId)
  if (fromContext) return fromContext

  // eventCoreId НЕ ИСПОЛЬЗУЕТСЯ как fallback - это auxiliary sessionId который может быть закрыт
  // Swing: всегда использует main core, не auxiliary core из WS события
  return null
}

function pinnedReloadApplies(context: ReloadPanelSessionContext | undefined | null): boolean {
  const pinnedSlot = String(context?.pinnedSlotId ?? '').trim()
  const targetSlot = String(context?.targetSlotId ?? '').trim()
  if (pinnedSlot && targetSlot && pinnedSlot !== targetSlot) return false
  if (targetSlot === MAIN_FRAME_SLOT_ID && pinnedSlot && pinnedSlot !== MAIN_FRAME_SLOT_ID) {
    return false
  }
  return true
}

/** Слот слушателя уже закрыт: не слать reload и не переносить ответ на другую форму. */
export function shouldDropPinnedReload(args: {
  pinnedSessionId?: unknown
  pinnedSlotId?: unknown
  slotStillPresent: boolean
}): boolean {
  const pinned = normalizePlatformSessionId(args.pinnedSessionId)
  const slot = String(args.pinnedSlotId ?? '').trim()
  if (!pinned || !slot) return false
  return !args.slotStillPresent
}

/** Ответ pin-reload нельзя записать в другой слот. Иначе закрытый документ подменяет главную. */
export function shouldRejectPinnedReloadRetarget(args: {
  pinnedSessionId?: unknown
  originSlotId?: unknown
  resolvedSlotId?: unknown
}): boolean {
  const pinned = normalizePlatformSessionId(args.pinnedSessionId)
  const origin = String(args.originSlotId ?? '').trim()
  const resolved = String(args.resolvedSlotId ?? '').trim()
  if (!pinned || !origin || !resolved) return false
  return origin !== resolved
}

/**
 * Не записывать на MAIN_FRAME session, которым reload не владел.
 * setPageData при смене session закрывает прежний core главной.
 */
export function chooseReloadStoredSessionId(args: {
  targetSlotId?: unknown
  previousSessionId?: unknown
  responseSessionId?: unknown
  requestSessionId?: unknown
}): unknown {
  const previous = args.previousSessionId
  const response = args.responseSessionId
  const responseText = response == null ? '' : String(response).trim()
  const responseUsable = responseText !== '' && responseText !== '-1'
  const next = responseUsable ? response : previous
  if (String(args.targetSlotId ?? '').trim() !== MAIN_FRAME_SLOT_ID) return next
  const previousId = normalizePlatformSessionId(previous)
  const requestId = normalizePlatformSessionId(args.requestSessionId)
  if (previousId && requestId && previousId !== requestId) return previous
  return next
}
